import React from 'react';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';

import SourceIcon from '@mui/icons-material/Source';
import { Box, Typography, Stack, Paper, IconButton, Radio } from '@mui/material';

import { Button } from 'shared';
import { Typewriter } from '../TypeWriter';
import {
    formatTransaction,
    formatInventory,
    getGeneratedReasoning,
} from 'components/Inventory/utils/aiMatchingHelpers';

const CurrentUserInputCard = ({
    currentCardIndex,
    data,
    renderingStage,
    advanceRenderingStage,
    handleEmailSourceClick,
    skipMatch,
    handleMatchClick,
    classes,
    currentSection,
    matchLoading,
    excludeLoading,
    handleExcludeClick,
    doNotShowAgainLoading,
    handleDoNotShowAgainClick,
}) => {
    if (
        currentSection !== 'needs_user_input' ||
        currentCardIndex === -1 ||
        currentCardIndex >= data.needs_user_input?.length
    )
        return null;

    const cardData = data.needs_user_input[currentCardIndex];
    const reasoning = cardData ? getGeneratedReasoning(cardData) : '';

    return (
        <Paper
            key={`current-input-${currentCardIndex}`}
            elevation={3}
            sx={classes.currentUserInputPaper}
        >
            <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Transactions
                </Typography>

                {renderingStage >= 1 && (
                    <Typewriter
                        items={cardData.transactions}
                        renderItem={(transaction) => (
                            <Box display="flex" alignItems="center" mb={1}>
                                <Box
                                    component="span"
                                    sx={classes.renderCurrentUserInputCardCardPaper}
                                />
                                <Typography variant="body1">
                                    {formatTransaction(transaction)}
                                </Typography>
                            </Box>
                        )}
                        delay={500}
                        animate={true}
                        onComplete={
                            renderingStage === 1 ? () => advanceRenderingStage() : undefined
                        }
                    />
                )}

                {renderingStage >= 2 && (
                    <>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 3 }}>
                            Inventories
                        </Typography>

                        <Typewriter
                            items={cardData.matched_inventories}
                            renderItem={(inventory) => (
                                <Typography variant="body1" mb={1}>
                                    {formatInventory(inventory)}
                                </Typography>
                            )}
                            delay={500}
                            animate={true}
                            onComplete={
                                renderingStage === 2 ? () => advanceRenderingStage() : undefined
                            }
                        />
                        {renderingStage > 2 && (
                            <Formik
                                initialValues={
                                    cardData.preserved_values ||
                                    cardData.matched_inventories.reduce((acc, inv) => {
                                        acc[`inventory_${inv.id}`] = '';
                                        return acc;
                                    }, {})
                                }
                                validationSchema={Yup.object(
                                    cardData.matched_inventories.reduce((acc, inv) => {
                                        acc[`inventory_${inv.id}`] = Yup.string().required(
                                            'Please select a transaction',
                                        );
                                        return acc;
                                    }, {}),
                                )}
                                onSubmit={(values) =>
                                    handleMatchClick(values, cardData, 'userInputCard')
                                }
                            >
                                {({ values, errors, touched, isSubmitting, handleChange }) => (
                                    <Form>
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mt={2}
                                            mb={1}
                                        >
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                Inventories
                                            </Typography>
                                            {/* <IconButton
                                                onClick={() => handleEmailSourceClick(cardData)}
                                                sx={{ padding: 0 }}
                                            >
                                                <SourceIcon fontSize="small" />
                                            </IconButton> */}
                                        </Box>

                                        {cardData.matched_inventories.map((inventory) => (
                                            <Box key={inventory.id} mb={2}>
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="medium"
                                                    mb={0.5}
                                                >
                                                    {formatInventory(inventory)}
                                                </Typography>

                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    flexWrap="wrap"
                                                    gap={2}
                                                    ml={1}
                                                >
                                                    {cardData.transactions.map((transaction) => (
                                                        <label
                                                            key={transaction.id}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Radio
                                                                name={`inventory_${inventory.id}`}
                                                                value={transaction.id.toString()}
                                                                checked={
                                                                    values[
                                                                        `inventory_${inventory.id}`
                                                                    ] === transaction.id.toString()
                                                                }
                                                                onChange={handleChange}
                                                                size="small"
                                                                sx={{
                                                                    padding: 0,
                                                                    marginRight: 1,
                                                                }}
                                                            />

                                                            <Typography variant="caption">
                                                                **** {transaction.card_last4}
                                                            </Typography>
                                                        </label>
                                                    ))}
                                                </Box>

                                                {touched[`inventory_${inventory.id}`] &&
                                                    errors[`inventory_${inventory.id}`] && (
                                                        <Typography
                                                            variant="body2"
                                                            color="error"
                                                            mt={0.5}
                                                        >
                                                            {errors[`inventory_${inventory.id}`]}
                                                        </Typography>
                                                    )}
                                            </Box>
                                        ))}

                                        <Typography
                                            variant="body2"
                                            sx={{ fontStyle: 'italic', color: '#666', mt: 2 }}
                                        >
                                            {reasoning}
                                        </Typography>

                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            justifyContent="space-between"
                                            mt={3}
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <Box>
                                                <Button
                                                    sx={classes.cancelButton}
                                                    text="Don't Show Again"
                                                    loading={doNotShowAgainLoading}
                                                    onClick={() => {
                                                        handleDoNotShowAgainClick(
                                                            cardData,
                                                            'userInputCard',
                                                        );
                                                    }}
                                                />
                                            </Box>

                                            <Box display="flex" gap={2}>
                                                <Button
                                                    sx={classes.cancelButton}
                                                    text="Exclude"
                                                    loading={excludeLoading}
                                                    onClick={() => {
                                                        handleExcludeClick(
                                                            cardData,
                                                            'userInputCard',
                                                        );
                                                    }}
                                                />

                                                <Button
                                                    text="Skip"
                                                    sx={classes.cancelButton}
                                                    onClick={() => {
                                                        skipMatch(cardData, 'userInputCard');
                                                    }}
                                                />

                                                <Button
                                                    variant="contained"
                                                    type="submit"
                                                    text="Match"
                                                    disabled={isSubmitting}
                                                    loading={matchLoading}
                                                />
                                            </Box>
                                        </Stack>
                                    </Form>
                                )}
                            </Formik>
                        )}
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default CurrentUserInputCard;
